import React from "react";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";

// Catches render errors so one broken component never blanks the app. Logs route/org/user/component stack — never record contents.
export default class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null, ref: null }; }
  static getDerivedStateFromError(error) { return { error, ref: Math.random().toString(36).slice(2, 8).toUpperCase() }; }
  componentDidCatch(error, info) {
    const ctx = { ref: this.state.ref, scope: this.props.scope || "app", route: window.location.pathname, message: String(error?.message || error).slice(0, 300), component_stack: String(info?.componentStack || "").slice(0, 2000), user_agent: navigator.userAgent.slice(0, 200) };
    console.error("[ChangeOS] render error", ctx);
    api.post("/client-errors", ctx).catch(() => {});
  }
  componentDidUpdate(prev) { if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null, ref: null }); }
  render() {
    if (!this.state.error) return this.props.children;
    const { title = "This part of the page hit a problem", inline = false } = this.props;
    return (
      <div role="alert" data-testid={`error-boundary-${this.props.scope || "app"}`} className={inline ? "rounded-2xl border border-warning-200 bg-warning-50 p-5 text-sm text-foreground" : "min-h-[60vh] flex items-center justify-center p-6"}>
        <div className={inline ? "" : "pp-card p-8 max-w-lg w-full"}>
          <div className="flex items-center gap-2 font-head text-lg font-extrabold text-foreground"><AlertTriangle size={18} className="text-warning-600" /> {title}</div>
          <p className="mt-2 text-subtle">Something in the display code failed. Your data is unaffected{this.props.preserves ? ` and ${this.props.preserves}` : ""}. Reloading usually clears it.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="error-reload" onClick={() => window.location.reload()} className="pp-btn-dark text-sm py-2">Reload</button>
            {!this.props.hideHome && <a href="/app" data-testid="error-home" className="pp-btn-ghost text-sm py-2">Back to dashboard</a>}
            {this.props.onRetry && <button data-testid="error-retry" onClick={() => { this.setState({ error: null, ref: null }); this.props.onRetry(); }} className="pp-btn-ghost text-sm py-2">Try again</button>}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Reference <span className="font-mono font-bold text-body" data-testid="error-ref">{this.state.ref}</span> — quote this if you contact us.</p>
        </div>
      </div>
    );
  }
}
